import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import styled, { css } from 'styled-components';
import { Flex, Icon } from 'ui';
import { useSelector } from 'react-redux';
import Sticky from 'common/sticky/sticky';
import ViewDropdownContainer from '../../containers/view-dropdown-container';
import FilterButton from '../../containers/filter-button-container';
import PlanningBoardMenu from '../../../planning-board/containers/planning-board-menu-container';
import { updateFilters } from '../../containers/worklist-actions';
import { additionalTabTypes } from '../../../utils/app-configs';

const BorderLeft = styled.div`
  border-left: 1px solid ${p => p.theme.colors.border};
`;

const StyledQuickActions = styled.div`
  padding: 0 10px;
`;

const Count = styled.p`
  margin-left: 20px;
  line-height: 34px;
`;

const StyledIcon = styled(Icon)`
  cursor: pointer;
  ${p => p.disabled && `
    cursor: not-allowed;
    color: gray;
  `}
`;

const IconWrapper = styled.div`
  height: 34px;
  display: flex;
  align-items: center;
  margin-left: 10px;
`;

// eslint-disable-next-line complexity
const FilterBar = ({
  menuVisible,
  isLoading,
  renderDynamicActions,
  data,
  isPlanningBoard = false,
  split = false,
  fullScreen,
  activeFilters,
  filters,
  isTickets = false,
  isNewWorklist = false,
  totalDataCount,
  fixedFilterValues = {},
  refresh,
}) => {
  const { isDataLoading, isLoadingMetaData } = useSelector(state => state.worklist);
  const isLoadingWorkgrid = isDataLoading || isLoadingMetaData;

  const extraStyles = css`
    margin-left: -35px !important;
    padding-left: 35px !important;
    background-color: rgb(245, 248, 250) !important;
    display: flex;
    margin-top: -15px;
    margin-right: -20px;
    margin-bottom: 0px;
    padding-top: 10px;
    padding-bottom: 10px;
    width: calc(100% + 35px) !important;
    ${isTickets ?
      `
                top: 109px !important;
                width: 100%;
              `
      : ''
    }

    ${split
      ? `
            top: 5px !important;
            margin-top: 0px !important;
            border-bottom: 1px solid #d9e0e6;
            width: 100%;
          `
      : ''
    }

    ${isPlanningBoard && !split
      ? `
            top: ${fullScreen ? '0px' : '109px'} !important;
            margin-top: 0px;
            border-bottom: 1px solid #d9e0e6;
          `
      : ''
    }
  `;

  const calculateTranslateY = () => {
    if (isPlanningBoard) {
      if (fullScreen) {
        return '0px';
      }
      return '-54px';
    } else if (isTickets) {
      return '-54px';
    }
    return '-106px';
  };

  return (
    <Sticky
      menuVisible={menuVisible}
      // eslint-disable-next-line
      translateYEnd={calculateTranslateY()}
      extraStyles={extraStyles}
      zIndex={75}
    >
      <Flex jcfs aifs>
        <StyledQuickActions data-test-id='worklist.toolbar.quickaction'>
          {renderDynamicActions()}
        </StyledQuickActions>
        {isTickets ?
          <ViewDropdownContainer isLoading={isLoading} isTickets={isTickets} /> :
          <BorderLeft>
            <ViewDropdownContainer isLoading={isLoading} />
          </BorderLeft>
        }
        <FilterButton
          isLoading={isLoading}
          activeFilters={activeFilters}
          filters={filters}
          fixedFilterValues={fixedFilterValues}
          urlKey='filterselect'
          updateCb={updateFilters}
          additionalActionTypes={[additionalTabTypes.columns]}
        />
        {isNewWorklist &&
          <Count data-test-id='worklist.toolbar.count'>
            <FormattedMessage
              id='common.toolbar.COUNT'
              values={{
                count: <b>{`rows ${data?.length ? 1 : 0} - ${data?.length || 0}`}</b>,
                total: totalDataCount || 0,
              }}
            />
          </Count>
        }
        <IconWrapper
          onClick={() => !isLoadingWorkgrid && refresh()}
          data-test-id='worklist.toolbar.refresh'
        >
          <StyledIcon
            className={`fa-solid fa-arrows-rotate ${isLoadingWorkgrid ? ' fa-spin' : ''}`}
            disabled={isLoadingWorkgrid}
          />
        </IconWrapper>
        {!split && isPlanningBoard ? <PlanningBoardMenu /> : null}
      </Flex>
    </Sticky>
  );
};

FilterBar.propTypes = {
  menuVisible: PropTypes.bool,
  isLoading: PropTypes.bool,
  renderDynamicActions: PropTypes.func,
  data: PropTypes.array,
  isPlanningBoard: PropTypes.bool,
  split: PropTypes.bool,
  fullScreen: PropTypes.bool,
  activeFilters: PropTypes.object,
  isTickets: PropTypes.bool,
  filters: PropTypes.array,
  isNewWorklist: PropTypes.bool,
  totalDataCount: PropTypes.number,
  fixedFilterValues: PropTypes.object,
  refresh: PropTypes.func,
};

export default injectIntl(FilterBar);
